import Aion from '@adoratorio/aion';
import Hammer from 'hammerjs';
import anime from 'animejs';

import Puzzle from './puzzle';
import audio from './audio';
import eventHub from './eventHub';
import { initials, animations } from './animations';
import { lerp, isTouchDevice } from './utils';

class Slider {
  constructor(puzzles, illustrations) {
    this.container = document.querySelector('main');
    this.itemsHTMLElement = puzzles;

    this.items = [];
    this.currentIndex = 0;
    this.isInTransition = false;

    this.swipeAudio = audio.get('swoosh');
    this.tutorialShown = false;

    this.illustrations = illustrations;
    this.itemsHTMLElement.forEach((puzzleEl, i) => {
      const puzzle = new Puzzle(puzzleEl, illustrations[i]);
      puzzle.startIndex = (puzzles.length - 1) - i;

      this.items.push(puzzle);
    });
    [this.currentItem] = this.items;

    if (!isTouchDevice()) {
      this.engine = new Aion();
      this.cardPosition = { x: 0, y: 0, rotation: 0 };
      this.lerpedPosition = { x: 0, y: 0, rotation: 0 };
      this.engine.add(() => this.panRender(), 'render-card');
    }

    this.hm = new Hammer(this.container);
    this.initialSet();
  }

  addEvents() {
    eventHub.$on('game-won', () => this.wonGame());
    if (!isTouchDevice()) {
      this.hm.on('panstart', (event) => this.panStartHandler(event));
    } else {
      this.hm.on('swipeleft swiperight', (event) => this.changeIndexPuzzle(1, event.type === 'swipeleft'));
    }
  }

  removeEvents() {
    if (!isTouchDevice()) {
      this.hm.off('pan');
      this.hm.off('panstart');
      this.hm.off('panend');
    } else {
      this.hm.off('swipeleft swiperight');
    }
  }

  panStartHandler() {
    this.toggleUI(false);
    this.hm.on('pan', (e) => this.panHandler(e));
    this.hm.on('panend', (e) => this.panEndHadler(e));
    this.engine.start();
  }

  panHandler(event) {
    this.cardPosition.x = event.deltaX;
    this.cardPosition.y = event.deltaY;
    this.cardPosition.rotation = event.deltaX * 0.03;
  }

  panRender() {
    const lerpedX = lerp(this.cardPosition.x, this.lerpedPosition.x, 0.8);
    const lerpedY = lerp(this.cardPosition.y, this.lerpedPosition.y, 0.8);
    const lerpedRotation = lerp(this.cardPosition.rotation, this.lerpedPosition.rotation, 0.8);

    const current = this.items[0].$el;
    anime.set(current, {
      translateX: `${lerpedX}px`,
      translateY: `${lerpedY}px`,
      rotateZ: lerpedRotation,
    });

    this.lerpedPosition.x = lerpedX;
    this.lerpedPosition.y = lerpedY;
    this.lerpedPosition.rotation = lerpedRotation;
  }

  panEndHadler(event) {
    this.engine.stop();
    this.cardPosition = { x: 0, y: 0, rotation: 0 };
    this.lerpedPosition = { x: 0, y: 0, rotation: 0 };
    const current = this.items[0].$el;
    if (event.distance > (this.container.getBoundingClientRect().width / 2.5)) {
      this.changeIndexPuzzle(+1, event.offsetDirection === Hammer.DIRECTION_LEFT);
    } else {
      anime({
        targets: current,
        translateX: 0,
        translateY: 0,
        rotateZ: 0,
      });
    }
    this.toggleUI(true);
  }

  changeIndexPuzzle(modificator, isPanReverse = false) {
    if (this.isInTransition) return;
    this.isInTransition = true;
    this.removeEvents();

    this.swipeAudio.ref.play();
    this.updateIndexes(modificator);

    const callback = (next = false) => {
      if (next) this.renderZIndex();
      this.isInTransition = false;
      this.addEvents();
    };

    if (modificator > 0) {
      this.items.push(this.items.shift());

      animations.get('slide-next')(this.items, isPanReverse, callback);
    } else if (modificator < 0) {
      this.items.unshift(this.items.pop());
      this.renderZIndex();
      animations.get('slide-prev')(this.items, callback);
    }

    [this.currentItem] = this.items;
  }

  updateIndexes(modificator) {
    const maxLength = this.items.length;

    this.currentIndex = this.currentIndex + modificator < 0
      ? maxLength - 1 : (this.currentIndex + modificator) % maxLength;
  }

  renderZIndex() {
    this.items.forEach((item) => {
      item.$el.style.zIndex = (item.startIndex + this.currentIndex) % 4;
    });
  }

  initialSet() {
    this.renderZIndex();
    initials.get('slider')(this.itemsHTMLElement);
  }

  enter(delay = 0) {
    animations.get('slider')(this.itemsHTMLElement, delay);
  }

  showTutorial() {
    if (this.tutorialShown) return;
    this.tutorialShown = true;
    const tutorial = document.querySelector('.puzzle__tutorial');
    anime.set(tutorial, { opacity: 0 });
    tutorial.style.display = 'flex';
    anime({
      targets: tutorial,
      easing: 'easeInOutCubic',
      opacity: [
        { value: 1, delay: 1650 },
        { value: 0, delay: 4000 },
      ],
      duration: 600,
    }).finished.then(() => { tutorial.style.display = 'none'; });
  }

  hideTutorial() {
    const tutorial = document.querySelector('.puzzle__tutorial');
    anime({
      targets: tutorial,
      easing: 'easeInOutCubic',
      opacity: 0,
      duration: 600,
    }).finished.then(() => { tutorial.style.display = 'none'; });
  }

  toggleUI(show = true) {
    anime({
      targets: [
        ...[...document.querySelectorAll('.puzzle-navigation')],
        document.querySelector('.puzzle-button'),
      ],
      opacity: show ? 1 : 0,
      duration: 400,
      easing: 'easeInOutCubic',
    });
  }

  startGame(delay = 0) {
    this.currentItem.$el.classList.add('in-game');
    this.removeEvents();
    eventHub.$emit('game-started');

    const tl = anime.timeline();

    tl.add({
      targets: this.currentItem.puzzleItemsHTMLElements,
      scale: { value: 0.8, easing: 'easeOutSine', duration: 120 },
      opacity: { value: 0, easing: 'easeOutSine', duration: 120 },
      delay: anime.stagger(85, { grid: [4, 4], from: 'center' }),
    }, delay)
      .add({
        targets: [this.items[1].$el, this.items[2].$el],
        scale: { value: 0.8, easing: 'easeOutCirc', duration: 500 },
        opacity: { value: 0, easing: 'easeOutCirc', duration: 500 },
        delay: anime.stagger(120, { from: 'last' }),
      }, delay + 100)
      .add({
        targets: this.currentItem.puzzleItemsHTMLElements,
        scale: { value: 1, easing: 'easeInOutQuad', duration: 160 },
        opacity: { value: 1, easing: 'easeInOutQuad', duration: 160 },
        delay: anime.stagger(85, { grid: [4, 4], from: 'center' }),
        begin: () => {
          this.currentItem.start();
        },
      }, delay + 800);
  }

  resetGame() {
    const tl = anime.timeline();
    tl.add({
      targets: this.currentItem.puzzleItemsHTMLElements,
      scale: { value: 0.8, easing: 'easeOutSine', duration: 120 },
      opacity: { value: 0, easing: 'easeOutSine', duration: 120 },
      delay: anime.stagger(85, { grid: [4, 4], from: 'center' }),
    })
      .add({
        targets: [this.items[1].$el, this.items[2].$el],
        scale: { value: 1, easing: 'easeOutCirc', duration: 500 },
        opacity: { value: 1, easing: 'easeOutCirc', duration: 500 },
        delay: anime.stagger(120, { from: 'last' }),
      }, 750)
      .add({
        targets: this.currentItem.puzzleItemsHTMLElements,
        scale: { value: 1, easing: 'easeInOutQuad', duration: 160 },
        opacity: { value: 1, easing: 'easeInOutQuad', duration: 160 },
        delay: anime.stagger(85, { grid: [4, 4], from: 'center' }),
        begin: () => {
          this.currentItem.reset();
        },
      }, 800);
  }

  wonGame() {
    anime({
      targets: [this.items[1].$el, this.items[2].$el],
      scale: { value: 1, easing: 'easeOutCirc', duration: 500 },
      opacity: { value: 1, easing: 'easeOutCirc', duration: 500 },
      delay: anime.stagger(120, { from: 'last' }),
    });
  }
}

export default Slider;
